import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DailyAudio, DailyProvider, useDevices, useVideoTrack } from '@daily-co/daily-react';
import { useAppointmentTypes } from '@features/appointment-types';
import type { AppointmentResponse } from '@features/appointments';
import { useNavigateToEncounter, useUpdateAppointmentMutation } from '@features/appointments';
import { type CreateEncounterDto, useCreateEncounterMutation } from '@features/encounters';
import { useGetTemplatesQuery } from '@features/templates';
import { useSubjectLanguageVariants } from '@features/user';
import { DailyCallService, useDailyCallState } from '@services/dailycall';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import { useFeature } from '@store/features/hooks';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useRecordingContext } from '../../../context/RecordingContext';
import { useAppointmentCollision } from '../../../hooks/useAppointmentCollision';
import { CallPanel } from '../components/CallPanel';
import { CompactPipView } from '../components/pip/CompactPipView';
import { PipStyleProvider } from '../components/pip/PipStyleProvider';
import { PipView } from '../components/pip/PipView';
import { PipErrorBoundary } from '../components/PipErrorBoundary';
import { TelehealthJoiningCallPanel } from '../components/TelehealthJoiningCallPanel';
import { endTelehealthSessionWithRetry } from '../endTelehealthSessionWithRetry';
import { useCallLayout } from '../hooks/useCallLayout';
import { usePictureInPicture } from '../hooks/usePictureInPicture';
import {
  addChatMessage,
  bindWaitingRoomLaunchToEncounter,
  clearActiveSession,
  clearChatMessages,
  clearWaitingRoomLaunchIntent,
  enterPreCall,
  exitPreCall,
  selectIsCameraOn,
  selectIsInPreCall,
  selectIsMicOn,
  selectSelectedCameraId,
  selectSelectedSpeakerId,
  selectSkipPreCallLobbyPreference,
  selectWaitingRoomLaunchIntent,
  setActiveSession,
  setChatPanelOpen,
  setSkipPreCallLobbyPreference,
  setWaitingRoomLaunchIntent,
  telehealthFeature,
} from '../store';
import type { ChatMessage, TelehealthEncounterRouteState, TelehealthSession } from '../types';
import type { WaitingRoomEntry } from '../waiting-room';
import {
  getWaitingRoomActionErrorCode,
  getWaitingRoomAdmitErrorMessage,
  useEndWaitingRoomSessionMutation,
  useJoinWaitingRoomEncounterMutation,
  useProviderWaitingRoomRuntime,
} from '../waiting-room';
import { WaitingRoomFloatingPanel } from '../waiting-room/components';
import { WaitingRoomAdmitDestinationModal } from '../waiting-room/components/WaitingRoomAdmitDestinationModal';
import type {
  WaitingRoomAdmitAppointmentDestination,
  WaitingRoomAdmitDestination,
} from '../waiting-room/components/waitingRoomAdmitDestinations';

import type {
  JoinCallParams,
  JoinWalkInCallParams,
  TelehealthCallManagerContextValue,
  TelehealthPhase,
} from './TelehealthCallManagerContext';
import { TelehealthCallManagerContext } from './TelehealthCallManagerContext';

const getWaitingRoomTelehealthRouteState = (
  skipPreCallLobby: boolean,
): TelehealthEncounterRouteState => ({
  telehealthLaunch: {
    source: 'waiting-room',
    skipPreCallLobby,
  },
});

const getAppointmentEncounterId = (appointment: AppointmentResponse) => {
  const encounter: unknown = appointment.encounter;

  if (!encounter) {
    return undefined;
  }

  if (typeof encounter === 'string') {
    return encounter;
  }

  if (typeof encounter === 'object' && 'encounter_id' in encounter) {
    const encounterId = (encounter as { encounter_id?: unknown }).encounter_id;
    return typeof encounterId === 'string' ? encounterId : undefined;
  }

  return undefined;
};

const toWaitingRoomAdmitAppointmentDestination = (
  appointment: AppointmentResponse,
): WaitingRoomAdmitAppointmentDestination => ({
  appointmentId: appointment.appointment_id,
  encounterId: getAppointmentEncounterId(appointment),
  patientName: appointment.patient?.name ?? appointment.display_title ?? null,
  encounterType: appointment.encounter_type ?? appointment.title ?? null,
  startTime: appointment.start_time,
  durationMinutes: appointment.duration,
});

const getDestinationTelemetry = (destination: WaitingRoomAdmitDestination) => {
  switch (destination.type) {
    case 'appointment':
      return {
        destination_type: destination.type,
        appointment_id: destination.appointment.appointmentId,
        encounter_id: destination.appointment.encounterId ?? undefined,
      };
    case 'active_call':
      return {
        destination_type: destination.type,
        encounter_id: destination.encounterId,
      };
    case 'walk_in':
      return {
        destination_type: destination.type,
      };
  }
};

const getWaitingRoomLaunchElapsedMs = (requestedAt?: number) =>
  requestedAt ? Math.max(0, Date.now() - requestedAt) : undefined;

const getTelemetryErrorCode = (error: unknown) =>
  error instanceof Error && error.name ? error.name : 'unknown_error';

const buildWalkInEncounterCreateRequest = ({
  defaultAppointmentType,
  defaultTemplate,
}: {
  defaultAppointmentType?: { encounter_type?: string | null; template_id?: string | null };
  defaultTemplate?: { display_name?: string | null; template_id?: string | null };
}): CreateEncounterDto => {
  const createRequest: CreateEncounterDto = {
    apply_defaults: true,
  };

  if (defaultAppointmentType) {
    createRequest.encounter_type = defaultAppointmentType.encounter_type;
    createRequest.template_id = defaultAppointmentType.template_id || defaultTemplate?.template_id;
  } else {
    createRequest.encounter_type = defaultTemplate?.display_name;
    createRequest.template_id = defaultTemplate?.template_id;
  }

  return createRequest;
};

export function TelehealthCallManager({ children }: { children: ReactNode }) {
  const isFeatureReady = useFeature(telehealthFeature);

  if (!isFeatureReady) {
    return null;
  }

  return <TelehealthCallManagerInner>{children}</TelehealthCallManagerInner>;
}

function TelehealthCallManagerInner({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { telehealth: telehealthTracking } = useEventTracking();
  const navigate = useNavigate();

  // Recording context integration
  const {
    setTelehealthSession: setRecordingTelehealthSession,
    cleanupTelehealthSession,
    requestRemoteStop,
    recordingStatus,
    audioInputDeviceService,
    setSelectedMicrophone,
    selectedMicrophone,
    initiateRecording,
    pauseRecording,
    resumeRecording,
  } = useRecordingContext();

  // State
  const [session, setSession] = useState<TelehealthSession | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [callObject, setCallObject] = useState<ReturnType<DailyCallService['getCallObject']>>(null);
  const dailyCallServiceRef = useRef<DailyCallService | null>(null);
  const leaveInProgressRef = useRef(false);
  // Sticky per-session signals for the "a real telehealth visit happened" commit.
  // Set once and never cleared mid-session, so a guest who joins then drops before
  // the provider ends the call is still counted (we must not under-mark telehealth).
  const sessionHadGuestRef = useRef(false);
  const sessionHadRecordingRef = useRef(false);
  const isInPreCall = useAppSelector(selectIsInPreCall);
  const isCameraOn = useAppSelector(selectIsCameraOn);
  const isMicOn = useAppSelector(selectIsMicOn);
  const selectedCameraId = useAppSelector(selectSelectedCameraId);
  const selectedSpeakerId = useAppSelector(selectSelectedSpeakerId);
  const skipAdmitDestinationPreCallLobby = useAppSelector(selectSkipPreCallLobbyPreference);
  const waitingRoomLaunchIntent = useAppSelector(selectWaitingRoomLaunchIntent);
  const [joinWaitingRoomEncounter] = useJoinWaitingRoomEncounterMutation();
  const [endWaitingRoomSession] = useEndWaitingRoomSessionMutation();
  const [createEncounter] = useCreateEncounterMutation();
  const [updateAppointment] = useUpdateAppointmentMutation();
  const { navigateToEncounter } = useNavigateToEncounter();
  const subjectLanguage = useSubjectLanguageVariants();
  const [admitDestinationEntry, setAdmitDestinationEntry] = useState<WaitingRoomEntry | null>(null);
  const [admitDestinationError, setAdmitDestinationError] = useState<string | null>(null);
  const [isCommittingAdmitDestination, setIsCommittingAdmitDestination] = useState(false);
  const openedDestinationPickerEntryRef = useRef<string | null>(null);
  const autoSelectedDestinationEntryRef = useRef<string | null>(null);
  const destinationLookupErrorEntryRef = useRef<string | null>(null);
  const shouldLoadAdmitDestinationData = Boolean(admitDestinationEntry);
  const { appointmentTypes, isLoading: isLoadingAppointmentTypes } = useAppointmentTypes(
    undefined,
    undefined,
    {
      skip: !shouldLoadAdmitDestinationData,
    },
  );
  const { data: templatesData, isLoading: isLoadingTemplates } = useGetTemplatesQuery(undefined, {
    skip: !shouldLoadAdmitDestinationData,
  });
  const templatesList = useMemo(() => {
    if (!templatesData?.templates) {
      return [];
    }

    return templatesData.templates.map(item => ({
      template_id: item.template.id,
      display_name: item.revision.title,
    }));
  }, [templatesData]);
  const {
    eligibleCurrentAppointments,
    eligibleOvertimeAppointments,
    eligibleNextAppointments,
    isError: isAppointmentDestinationsError,
    isLoading: isLoadingAppointmentDestinations,
  } = useAppointmentCollision({ skip: !shouldLoadAdmitDestinationData });

  // Subscribe to DailyCallService state for Media Session config
  const callState = useDailyCallState(dailyCallServiceRef.current);

  // Remote video track for hidden <video> (Safari classic PiP + Chrome auto-PiP).
  // PiP is a single-stream surface by browser constraint — track the active speaker
  // so the PiP window follows whoever is talking in 3+ participant calls.
  const layout = useCallLayout();
  const remoteVideoState = useVideoTrack(layout.activeSpeakerId ?? '');

  // Derived
  const phase: TelehealthPhase = !session
    ? isJoining
      ? 'joining'
      : 'idle'
    : isInPreCall
      ? 'precall'
      : 'call';

  const activeEncounterId = session?.encounterId ?? null;
  const activeAppointmentId = session?.appointmentId ?? null;

  const waitingRoom = useProviderWaitingRoomRuntime();
  const appointmentDestinations = useMemo(
    () =>
      [
        ...eligibleCurrentAppointments,
        ...eligibleOvertimeAppointments,
        ...eligibleNextAppointments,
      ].map(({ appointment }) => toWaitingRoomAdmitAppointmentDestination(appointment)),
    [eligibleCurrentAppointments, eligibleNextAppointments, eligibleOvertimeAppointments],
  );
  const appointmentById = useMemo(() => {
    const appointments = [
      ...eligibleCurrentAppointments,
      ...eligibleOvertimeAppointments,
      ...eligibleNextAppointments,
    ].map(({ appointment }) => appointment);

    return new Map(appointments.map(appointment => [appointment.appointment_id, appointment]));
  }, [eligibleCurrentAppointments, eligibleNextAppointments, eligibleOvertimeAppointments]);
  const isLoadingAdmitDestinationData =
    isLoadingAppointmentDestinations ||
    (!isAppointmentDestinationsError &&
      appointmentDestinations.length === 0 &&
      (isLoadingAppointmentTypes || isLoadingTemplates));

  const admitWaitingRoomLaunchIntent = useCallback(
    async (encounterId: string | undefined | null) => {
      if (
        !encounterId ||
        !waitingRoomLaunchIntent?.entryId ||
        waitingRoomLaunchIntent.encounterId !== encounterId
      ) {
        return;
      }

      const entry = waitingRoom.entries.find(
        waitingRoomEntry =>
          waitingRoomEntry.waiting_room_queue_entry_id === waitingRoomLaunchIntent.entryId,
      );
      const elapsedMs = getWaitingRoomLaunchElapsedMs(waitingRoomLaunchIntent.requestedAt);
      const telemetryMetadata = {
        entry_id: waitingRoomLaunchIntent.entryId,
        entry_state: entry?.current_state,
        encounter_id: encounterId,
        elapsed_ms: elapsedMs,
      };

      telehealthTracking.waitingRoomAutoAdmitStarted(telemetryMetadata);
      SentryService.addBreadcrumb({
        category: 'telehealth.waiting_room',
        message: 'Waiting room auto-admit started',
        level: 'info',
        data: telemetryMetadata,
      });

      try {
        await waitingRoom.admitPatient(waitingRoomLaunchIntent.entryId, encounterId);
        telehealthTracking.waitingRoomAutoAdmitSucceeded(telemetryMetadata);
        SentryService.addBreadcrumb({
          category: 'telehealth.waiting_room',
          message: 'Waiting room auto-admit succeeded',
          level: 'info',
          data: telemetryMetadata,
        });
        enqueueSnackbar(`${waitingRoomLaunchIntent.patientName} was added to the call.`, {
          variant: 'success',
        });
        dispatch(clearWaitingRoomLaunchIntent());
      } catch (admitError) {
        const errorCode = getWaitingRoomActionErrorCode(admitError);
        telehealthTracking.waitingRoomAutoAdmitFailed({
          ...telemetryMetadata,
          error_code: errorCode,
        });
        SentryService.addBreadcrumb({
          category: 'telehealth.waiting_room',
          message: 'Waiting room auto-admit failed',
          level: 'error',
          data: {
            ...telemetryMetadata,
            error_code: errorCode,
          },
        });
        SentryService.captureException(admitError, {
          tags: { component: 'telehealth_waiting_room_auto_admit' },
          extra: {
            entry_id: waitingRoomLaunchIntent.entryId,
            entry_state: entry?.current_state,
            encounter_id: encounterId,
            elapsed_ms: elapsedMs,
            error_code: errorCode,
          },
        });
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomAdmitErrorMessage(admitError, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [
      dispatch,
      enqueueSnackbar,
      subjectLanguage,
      telehealthTracking,
      waitingRoom,
      waitingRoomLaunchIntent,
    ],
  );

  // --- PiP ---

  // Handler refs for Media Session — avoids stale closures in usePictureInPicture effects
  const toggleMicRef = useRef<() => void>(() => {});
  const toggleVideoRef = useRef<() => void>(() => {});
  const endCallRef = useRef<() => void>(() => {});

  const pip = usePictureInPicture({
    mediaSession: {
      enabled: phase === 'call',
      onToggleMic: () => toggleMicRef.current(),
      onToggleCamera: () => toggleVideoRef.current(),
      onHangup: () => endCallRef.current(),
      isMicActive: callState.localAudioEnabled,
      isCameraActive: callState.localVideoEnabled,
    },
    onClassicTrackLost: () => remoteVideoState?.persistentTrack ?? null,
    onOpened: metadata => telehealthTracking.pipOpened(metadata),
    onClosed: metadata => telehealthTracking.pipClosed(metadata),
    onError: metadata => telehealthTracking.pipError(metadata),
    onAutoTriggered: metadata => telehealthTracking.pipAutoTriggered(metadata),
  });

  // Mini-player visibility: shows on all pages during active call (PiP takes priority)
  const shouldShowCallPanel = phase === 'call' && !pip.isActive;

  // Track, per JotMeet session, whether a guest ever joined. Reset when a new
  // call object is established, then set via Daily's `participant-joined` (plus an
  // initial snapshot, in case a guest is already present when we subscribe) so it
  // stays true even if the guest leaves before the provider ends the call.
  useEffect(() => {
    if (!callObject) return;
    sessionHadGuestRef.current = false;
    sessionHadRecordingRef.current = false;

    const markGuest = () => {
      sessionHadGuestRef.current = true;
    };
    const remoteNow = Object.values(callObject.participants() ?? {}).filter(p => !p.local).length;
    if (remoteNow > 0) sessionHadGuestRef.current = true;

    callObject.on('participant-joined', markGuest);
    return () => {
      callObject.off('participant-joined', markGuest);
    };
  }, [callObject]);

  // Recording may start/stop several times within a session; once it has run the
  // flag stays set for the rest of the session.
  useEffect(() => {
    if (recordingStatus.isRecording) sessionHadRecordingRef.current = true;
  }, [recordingStatus.isRecording]);

  // --- Handlers ---

  // `handleLeaveInternal` runs the shared leave/cleanup path. The
  // `explicit` flag distinguishes the two callers:
  //
  //  - `true` (default) — provider clicked end-call (button, dialog,
  //    PiP hangup). We tell the backend to delete the Daily room and
  //    complete the queue entries (GDN-1538), emit the Statsig
  //    `session_end_initiated` event, then do local cleanup.
  //  - `false` — Daily fired an error/disconnect event and the
  //    DailyCallService transitioned to `idle` (network drop, tab
  //    backgrounded, etc.). Do local cleanup ONLY. Calling end-session
  //    here would mistake a reconnect-eligible network drop for an
  //    intentful end and tear down the room behind the provider's
  //    back.
  const handleLeaveInternal = useCallback(
    async ({ explicit = true }: { explicit?: boolean } = {}) => {
      if (leaveInProgressRef.current) return;
      leaveInProgressRef.current = true;

      // Capture the encounter context BEFORE we tear down `session` —
      // local cleanup below clears it and the end-session call below
      // would otherwise see a stale closure.
      const encounterIdForEndSession = session?.encounterId ?? null;
      const appointmentIdForCommit = session?.appointmentId ?? null;
      const callObjectAtConfirm = dailyCallServiceRef.current?.getCallObject() ?? null;
      const remoteParticipantCount = callObjectAtConfirm
        ? Object.values(callObjectAtConfirm.participants() ?? {}).filter(p => !p.local).length
        : 0;

      // Stop recording before cleaning up telehealth session.
      // Read isRecording from localStorage (source of truth) to avoid stale closures.
      // Uses requestRemoteStop because it reads ownership from localStorage and calls
      // performStopRecording via ref — avoids stale closure issues.
      const storedStatus = localStorage.getItem('recordingStatus');
      const isCurrentlyRecording = storedStatus
        ? (JSON.parse(storedStatus) as { isRecording?: boolean }).isRecording === true
        : false;

      if (explicit) {
        // Statsig: provider confirmed end-call. Fired exactly once per
        // explicit leave (the ref guard above gates re-entry), regardless
        // of whether end-session succeeds. Captures the state at confirm
        // so we can answer "how often does a provider end with a patient
        // still attached / recording running?" without joining against
        // ProductEvents. No PHI — encounter_id only.
        telehealthTracking.sessionEndInitiated({
          encounter_id: encounterIdForEndSession ?? undefined,
          had_other_participants: remoteParticipantCount > 0,
          recording_active_at_confirm: isCurrentlyRecording,
        });
      }

      // Commit JotMeet delivery on the appointment only when a real visit
      // happened — a guest joined at some point AND a recording ran. Merely
      // opening or toggling the panel never reaches this gate, so provider
      // self-tests are never marked as JotMeet visits.
      if (
        explicit &&
        appointmentIdForCommit &&
        sessionHadGuestRef.current &&
        sessionHadRecordingRef.current
      ) {
        void updateAppointment({
          appointmentId: appointmentIdForCommit,
          appointmentUpdate: {
            modality: 'video',
            telehealth: true,
            use_jotmeet: true,
          },
        });
      }

      try {
        if (isCurrentlyRecording) {
          try {
            await requestRemoteStop();
          } catch {
            // Best-effort stop — proceed with cleanup regardless
          }
        }

        // GDN-1538: tell the backend to delete the Daily room and
        // mark the queue entries COMPLETED before we tear down local
        // state. Capped at ~3s; on failure we Sentry + proceed so the
        // provider is NEVER blocked from leaving by a slow backend.
        // Skipped on the non-explicit path so Daily reconnects (which
        // surface as the same `idle` snapshot we use here) don't
        // mistakenly destroy the room.
        if (explicit && encounterIdForEndSession) {
          const result = await endTelehealthSessionWithRetry({
            call: () =>
              endWaitingRoomSession({
                encounterId: encounterIdForEndSession,
              }).unwrap(),
          });
          if (!result.ok) {
            SentryService.captureException(result.error, {
              tags: {
                component: 'telehealth_end_session',
                end_session_failure: result.reason,
              },
              extra: {
                encounter_id: encounterIdForEndSession,
                attempts: result.attempts,
              },
            });
          }
        }

        await cleanupTelehealthSession();
      } finally {
        dailyCallServiceRef.current = null;
        setCallObject(null);
        setSession(null);
        dispatch(clearActiveSession());
        dispatch(exitPreCall());
        dispatch(clearChatMessages());
        dispatch(setChatPanelOpen(false));
        dispatch(clearWaitingRoomLaunchIntent());
        leaveInProgressRef.current = false;
      }
    },
    [
      cleanupTelehealthSession,
      dispatch,
      endWaitingRoomSession,
      requestRemoteStop,
      session,
      telehealthTracking,
      updateAppointment,
    ],
  );

  const trackCallLeftAndLeave = useCallback(
    (source: string) => {
      telehealthTracking.callLeft({
        appointment_id: session?.appointmentId,
        encounter_id: session?.encounterId,
        source,
      });
      void handleLeaveInternal();
    },
    [handleLeaveInternal, session?.appointmentId, session?.encounterId, telehealthTracking],
  );

  // Auto-leave call when recording stops (e.g. remote tab stopped the recording).
  // In telehealth mode, the call and recording are unified — if the recording ends,
  // the call should end too.
  const wasRecordingRef = useRef(false);
  useEffect(() => {
    if (recordingStatus.isRecording) {
      wasRecordingRef.current = true;
      return;
    }
    // Recording just stopped while in an active call → leave
    if (wasRecordingRef.current && phase === 'call' && session) {
      wasRecordingRef.current = false;
      if (leaveInProgressRef.current) {
        return;
      }
      // Defer to avoid acting during the stop transition
      const timer = setTimeout(() => {
        void (async () => {
          if (leaveInProgressRef.current) return;
          leaveInProgressRef.current = true;
          try {
            await cleanupTelehealthSession();
          } finally {
            dailyCallServiceRef.current = null;
            setCallObject(null);
            setSession(null);
            dispatch(clearActiveSession());
            dispatch(exitPreCall());
            dispatch(clearChatMessages());
            dispatch(setChatPanelOpen(false));
            dispatch(clearWaitingRoomLaunchIntent());
            leaveInProgressRef.current = false;
          }
        })();
      }, 500);
      return () => clearTimeout(timer);
    }
    wasRecordingRef.current = false;
  }, [recordingStatus.isRecording, phase, session, cleanupTelehealthSession, dispatch]);

  // Sync handler refs after handler definitions
  toggleMicRef.current = () => {
    dailyCallServiceRef.current?.setLocalAudio(!callState.localAudioEnabled);
  };
  toggleVideoRef.current = () => {
    dailyCallServiceRef.current?.setLocalVideo(!callState.localVideoEnabled);
  };
  endCallRef.current = () => {
    trackCallLeftAndLeave('media_session');
  };

  // Close PiP and bring opener tab to foreground (for user-initiated close buttons)
  const handlePipClose = useCallback(async () => {
    window.focus();
    await pip.close('return_to_encounter');
  }, [pip]);

  // Close PiP, open chat panel, and navigate to encounter
  const handleOpenChatFromPip = useCallback(async () => {
    window.focus();
    await pip.close('open_chat');
    dispatch(setChatPanelOpen(true));
    if (activeEncounterId) {
      navigate(`/encounters/${activeEncounterId}`);
    }
  }, [pip, dispatch, activeEncounterId, navigate]);

  const createEncounterSession = useCallback(
    async ({ appointmentId, encounterId }: JoinCallParams) => {
      const result = await joinWaitingRoomEncounter({ encounterId }).unwrap();

      const service = new DailyCallService();
      service.initialize({
        audioSource: selectedMicrophone?.deviceId,
        videoSource: selectedCameraId ?? undefined,
      });
      dailyCallServiceRef.current = service;
      setCallObject(service.getCallObject());

      setRecordingTelehealthSession({
        dailyCallService: service,
        roomUrl: result.room_url,
        token: result.owner_token,
      });

      const newSession: TelehealthSession = {
        appointmentId,
        encounterId: result.encounter_id,
        roomUrl: result.room_url,
        roomName: result.room_name,
        token: result.owner_token,
        isOwner: true,
        expiresAt: result.expires_at ?? null,
      };

      return { service, newSession };
    },
    [
      joinWaitingRoomEncounter,
      selectedCameraId,
      selectedMicrophone?.deviceId,
      setRecordingTelehealthSession,
    ],
  );

  const applyLocalMediaPreferences = useCallback(
    async (service: DailyCallService) => {
      if (selectedCameraId) {
        try {
          await service.setVideoDevice(selectedCameraId);
        } catch (error) {
          SentryService.captureException(error, {
            tags: { component: 'telehealth_camera_preference' },
          });
          enqueueSnackbar('Selected camera is unavailable. Using the default camera.', {
            variant: 'warning',
          });
        }
      }
      service.setLocalVideo(isCameraOn);
      service.setLocalAudio(isMicOn);
    },
    [enqueueSnackbar, isCameraOn, isMicOn, selectedCameraId],
  );

  const joinCall = useCallback(
    async (params: JoinCallParams) => {
      setIsJoining(true);
      const source = params.source ?? 'pre_call_lobby';
      try {
        const { newSession } = await createEncounterSession(params);
        telehealthTracking.joinAttempted({
          appointment_id: newSession.appointmentId,
          encounter_id: newSession.encounterId,
          source,
          outcome: 'pre_call_started',
        });
        setSession(newSession);
        dispatch(setActiveSession(newSession));
        dispatch(enterPreCall());
      } catch (error) {
        await cleanupTelehealthSession();
        dailyCallServiceRef.current = null;
        setCallObject(null);
        SentryService.captureException(error, {
          tags: { component: 'telehealth_join' },
        });
        telehealthTracking.joinAttempted({
          appointment_id: params.appointmentId,
          encounter_id: params.encounterId,
          source,
          outcome: 'failed',
          error_code: getTelemetryErrorCode(error),
        });
        enqueueSnackbar('Failed to join telehealth session', {
          variant: 'error',
        });
      } finally {
        setIsJoining(false);
      }
    },
    [
      cleanupTelehealthSession,
      createEncounterSession,
      dispatch,
      enqueueSnackbar,
      telehealthTracking,
    ],
  );

  const joinCallDirect = useCallback(
    async (params: JoinCallParams) => {
      setIsJoining(true);
      const source = params.source ?? 'direct_join';
      try {
        const { service, newSession } = await createEncounterSession(params);

        // Join Daily room and start recording BEFORE setting session.
        // Session set triggers phase → 'call' which shows CallPanel.
        // By deferring, the join card stays with "Joining..." until everything is ready.
        await service.join(newSession.roomUrl, newSession.token);
        telehealthTracking.joinAttempted({
          appointment_id: newSession.appointmentId,
          encounter_id: newSession.encounterId,
          source,
          outcome: 'connected',
        });
        telehealthTracking.callConnected({
          appointment_id: newSession.appointmentId,
          encounter_id: newSession.encounterId,
          source,
        });

        // Auto-start recording before revealing the call UI
        try {
          const started = await initiateRecording();
          if (!started) {
            // GDN-2784: a declined before-start gate (or a not-ready pipeline)
            // returns false rather than throwing — the call must not run
            // silently unrecorded.
            enqueueSnackbar(
              'Recording did not start automatically. You can start it manually.',
              { variant: 'warning' },
            );
          }
        } catch (recordingError) {
          SentryService.captureException(recordingError, {
            tags: { component: 'telehealth_recording_autostart' },
          });
          enqueueSnackbar('Recording failed to start automatically. You can start it manually.', {
            variant: 'warning',
          });
        }

        await applyLocalMediaPreferences(service);

        await admitWaitingRoomLaunchIntent(newSession.encounterId);

        // Now reveal the call UI — phase transitions to 'call'
        setSession(newSession);
        dispatch(setActiveSession(newSession));
      } catch (error) {
        await cleanupTelehealthSession();
        dailyCallServiceRef.current = null;
        setCallObject(null);
        SentryService.captureException(error, {
          tags: { component: 'telehealth_join_direct' },
        });
        telehealthTracking.joinAttempted({
          appointment_id: params.appointmentId,
          encounter_id: params.encounterId,
          source,
          outcome: 'failed',
          error_code: getTelemetryErrorCode(error),
        });
        enqueueSnackbar('Failed to join telehealth session', {
          variant: 'error',
        });
      } finally {
        setIsJoining(false);
      }
    },
    [
      cleanupTelehealthSession,
      admitWaitingRoomLaunchIntent,
      applyLocalMediaPreferences,
      createEncounterSession,
      enqueueSnackbar,
      dispatch,
      telehealthTracking,
      initiateRecording,
    ],
  );

  const joinWalkInCall = useCallback(
    async ({ encounterId, roomUrl, token, roomName, expiresAt }: JoinWalkInCallParams) => {
      if (session?.encounterId === encounterId && phase === 'call') return;

      setIsJoining(true);
      try {
        const service = new DailyCallService();
        service.initialize({
          audioSource: selectedMicrophone?.deviceId,
          videoSource: selectedCameraId ?? undefined,
        });
        dailyCallServiceRef.current = service;
        setCallObject(service.getCallObject());

        setRecordingTelehealthSession({
          dailyCallService: service,
          roomUrl,
          token,
        });

        const newSession: TelehealthSession = {
          encounterId,
          roomUrl,
          roomName: roomName ?? '',
          token,
          isOwner: true,
          expiresAt: expiresAt ?? null,
        };

        await service.join(newSession.roomUrl, newSession.token);
        telehealthTracking.joinAttempted({
          encounter_id: newSession.encounterId,
          source: 'walk_in',
          outcome: 'connected',
        });
        telehealthTracking.callConnected({
          encounter_id: newSession.encounterId,
          source: 'walk_in',
        });

        try {
          const started = await initiateRecording();
          if (!started) {
            // GDN-2784: a declined before-start gate (or a not-ready pipeline)
            // returns false rather than throwing — the call must not run
            // silently unrecorded.
            enqueueSnackbar(
              'Recording did not start automatically. You can start it manually.',
              { variant: 'warning' },
            );
          }
        } catch (recordingError) {
          SentryService.captureException(recordingError, {
            tags: { component: 'telehealth_walk_in_recording_autostart' },
          });
          enqueueSnackbar('Recording failed to start automatically. You can start it manually.', {
            variant: 'warning',
          });
        }

        await applyLocalMediaPreferences(service);

        setSession(newSession);
        dispatch(setActiveSession(newSession));
        dispatch(exitPreCall());
      } catch (error) {
        await cleanupTelehealthSession();
        dailyCallServiceRef.current = null;
        setCallObject(null);
        SentryService.captureException(error, {
          tags: { component: 'telehealth_join_walk_in' },
        });
        telehealthTracking.joinAttempted({
          encounter_id: encounterId,
          source: 'walk_in',
          outcome: 'failed',
          error_code: getTelemetryErrorCode(error),
        });
        enqueueSnackbar('Failed to join walk-in telehealth session', {
          variant: 'error',
        });
        throw error;
      } finally {
        setIsJoining(false);
      }
    },
    [
      cleanupTelehealthSession,
      dispatch,
      enqueueSnackbar,
      initiateRecording,
      phase,
      applyLocalMediaPreferences,
      selectedCameraId,
      selectedMicrophone?.deviceId,
      session?.encounterId,
      setRecordingTelehealthSession,
      telehealthTracking,
    ],
  );

  const launchWaitingRoomDestination = useCallback(
    async (
      entry: WaitingRoomEntry,
      destination: WaitingRoomAdmitDestination,
      options: { trackSelection?: boolean } = {},
    ) => {
      const shouldTrackSelection = options.trackSelection ?? true;
      const destinationTelemetry = getDestinationTelemetry(destination);
      const telemetryMetadata = {
        entry_id: entry.waiting_room_queue_entry_id,
        entry_state: entry.current_state,
        skip_pre_call_lobby: skipAdmitDestinationPreCallLobby,
        ...destinationTelemetry,
      };

      if (shouldTrackSelection) {
        telehealthTracking.waitingRoomDestinationSelected(telemetryMetadata);
        SentryService.addBreadcrumb({
          category: 'telehealth.waiting_room',
          message: 'Waiting room destination selected',
          level: 'info',
          data: telemetryMetadata,
        });
      }

      if (destination.type === 'active_call') {
        try {
          await waitingRoom.admitPatient(
            entry.waiting_room_queue_entry_id,
            destination.encounterId,
          );
          enqueueSnackbar(`${entry.patient_name} was added to the call.`, {
            variant: 'success',
          });
          await waitingRoom.refreshQueue().catch(() => undefined);
          return;
        } catch (error) {
          const errorCode = getWaitingRoomActionErrorCode(error);
          telehealthTracking.waitingRoomDestinationLaunchFailed({
            ...telemetryMetadata,
            error_code: errorCode,
          });
          SentryService.captureException(error, {
            tags: { component: 'telehealth_waiting_room_destination_active_call' },
            extra: {
              entry_id: entry.waiting_room_queue_entry_id,
              entry_state: entry.current_state,
              encounter_id: destination.encounterId,
              error_code: errorCode,
            },
          });
          throw error;
        }
      }

      dispatch(
        setWaitingRoomLaunchIntent({
          entryId: entry.waiting_room_queue_entry_id,
          patientName: entry.patient_name,
        }),
      );

      try {
        if (destination.type === 'appointment') {
          const appointment =
            appointmentById.get(destination.appointment.appointmentId) ??
            ({
              appointment_id: destination.appointment.appointmentId,
              encounter: destination.appointment.encounterId
                ? { encounter_id: destination.appointment.encounterId }
                : null,
            } as AppointmentResponse);

          const existingEncounterId =
            destination.appointment.encounterId ?? getAppointmentEncounterId(appointment);

          if (existingEncounterId) {
            dispatch(bindWaitingRoomLaunchToEncounter(existingEncounterId));
          }

          const navigationResult = await navigateToEncounter(appointment, {
            state: getWaitingRoomTelehealthRouteState(skipAdmitDestinationPreCallLobby),
            allowNoteRedirect: false,
          });
          if (navigationResult.encounterId) {
            dispatch(bindWaitingRoomLaunchToEncounter(navigationResult.encounterId));
          }
          return;
        }

        const defaultAppointmentType = appointmentTypes?.[0];
        const defaultTemplate = templatesList[0];
        const newEncounter = await createEncounter({
          createEncounterRequest: buildWalkInEncounterCreateRequest({
            defaultAppointmentType,
            defaultTemplate,
          }),
        }).unwrap();

        dispatch(bindWaitingRoomLaunchToEncounter(newEncounter.encounter_id));

        // appointmentTypeSuggested: the walk-in create request pre-filled the
        // provider's most-used type — surface the "Suggested" label. (GDN-2228)
        navigate(`/encounters/${newEncounter.encounter_id}`, {
          state: {
            ...getWaitingRoomTelehealthRouteState(skipAdmitDestinationPreCallLobby),
            appointmentTypeSuggested: !!defaultAppointmentType,
          },
        });
      } catch (error) {
        const errorCode = getWaitingRoomActionErrorCode(error);
        telehealthTracking.waitingRoomDestinationLaunchFailed({
          ...telemetryMetadata,
          error_code: errorCode,
        });
        SentryService.addBreadcrumb({
          category: 'telehealth.waiting_room',
          message: 'Waiting room destination launch failed',
          level: 'error',
          data: {
            ...telemetryMetadata,
            error_code: errorCode,
          },
        });
        SentryService.captureException(error, {
          tags: { component: 'telehealth_waiting_room_launch' },
          extra: {
            entry_id: entry.waiting_room_queue_entry_id,
            entry_state: entry.current_state,
            destination_type: destination.type,
            appointment_id:
              destination.type === 'appointment'
                ? destination.appointment.appointmentId
                : undefined,
            encounter_id:
              destination.type === 'appointment' ? destination.appointment.encounterId : undefined,
            error_code: errorCode,
          },
        });
        dispatch(clearWaitingRoomLaunchIntent());
        throw error;
      }
    },
    [
      appointmentById,
      appointmentTypes,
      createEncounter,
      dispatch,
      enqueueSnackbar,
      navigate,
      navigateToEncounter,
      skipAdmitDestinationPreCallLobby,
      telehealthTracking,
      templatesList,
      waitingRoom,
    ],
  );

  const handleAdmitDestinationSubmit = useCallback(
    async (destination: WaitingRoomAdmitDestination) => {
      if (!admitDestinationEntry) {
        return;
      }

      setIsCommittingAdmitDestination(true);
      setAdmitDestinationError(null);

      try {
        await launchWaitingRoomDestination(admitDestinationEntry, destination);
        setAdmitDestinationEntry(null);
      } catch (error) {
        setAdmitDestinationError(
          destination.type === 'active_call'
            ? getWaitingRoomAdmitErrorMessage(error, subjectLanguage)
            : 'Could not open the selected telehealth destination. Please try again.',
        );
        enqueueSnackbar('Could not open the selected telehealth destination.', {
          variant: 'error',
        });
      } finally {
        setIsCommittingAdmitDestination(false);
      }
    },
    [admitDestinationEntry, enqueueSnackbar, launchWaitingRoomDestination, subjectLanguage],
  );

  const handleAdmitDestinationClose = useCallback(() => {
    if (!admitDestinationEntry || isCommittingAdmitDestination) {
      return;
    }

    telehealthTracking.waitingRoomDestinationCancelled({
      entry_id: admitDestinationEntry.waiting_room_queue_entry_id,
      entry_state: admitDestinationEntry.current_state,
      destination_count: appointmentDestinations.length + 1,
      appointment_count: appointmentDestinations.length,
    });
    SentryService.addBreadcrumb({
      category: 'telehealth.waiting_room',
      message: 'Waiting room destination picker cancelled',
      level: 'info',
      data: {
        entry_id: admitDestinationEntry.waiting_room_queue_entry_id,
        entry_state: admitDestinationEntry.current_state,
        destination_count: appointmentDestinations.length + 1,
        appointment_count: appointmentDestinations.length,
      },
    });
    setAdmitDestinationEntry(null);
    setAdmitDestinationError(null);
  }, [
    admitDestinationEntry,
    appointmentDestinations.length,
    isCommittingAdmitDestination,
    telehealthTracking,
  ]);

  const handleSkipPreCallLobbyChange = useCallback(
    (skipPreCallLobby: boolean) => {
      dispatch(setSkipPreCallLobbyPreference(skipPreCallLobby));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!admitDestinationEntry || isLoadingAppointmentDestinations) {
      return;
    }

    const entryId = admitDestinationEntry.waiting_room_queue_entry_id;

    if (isAppointmentDestinationsError) {
      if (destinationLookupErrorEntryRef.current === entryId) {
        return;
      }

      destinationLookupErrorEntryRef.current = entryId;
      setAdmitDestinationError(
        'Could not load scheduled telehealth destinations. Please try again.',
      );
      SentryService.addBreadcrumb({
        category: 'telehealth.waiting_room',
        message: 'Waiting room destination lookup failed',
        level: 'error',
        data: {
          entry_id: entryId,
          entry_state: admitDestinationEntry.current_state,
        },
      });
      return;
    }

    if (appointmentDestinations.length === 0) {
      if (isLoadingAppointmentTypes || isLoadingTemplates) {
        return;
      }

      if (autoSelectedDestinationEntryRef.current === entryId) {
        return;
      }

      autoSelectedDestinationEntryRef.current = entryId;
      const destination: WaitingRoomAdmitDestination = {
        type: 'walk_in',
        label: '',
        description: '',
      };
      telehealthTracking.waitingRoomDestinationAutoSelected({
        entry_id: entryId,
        entry_state: admitDestinationEntry.current_state,
        destination_type: 'walk_in',
        skip_pre_call_lobby: skipAdmitDestinationPreCallLobby,
      });
      SentryService.addBreadcrumb({
        category: 'telehealth.waiting_room',
        message: 'Waiting room destination auto-selected',
        level: 'info',
        data: {
          entry_id: entryId,
          entry_state: admitDestinationEntry.current_state,
          destination_type: 'walk_in',
        },
      });
      setAdmitDestinationEntry(null);
      void launchWaitingRoomDestination(admitDestinationEntry, destination, {
        trackSelection: false,
      }).catch(() => {
        enqueueSnackbar('Could not open a telehealth encounter for this waiting-room entry.', {
          variant: 'error',
        });
      });
      return;
    }

    if (openedDestinationPickerEntryRef.current === entryId) {
      return;
    }

    openedDestinationPickerEntryRef.current = entryId;
    const pickerTelemetry = {
      entry_id: entryId,
      entry_state: admitDestinationEntry.current_state,
      destination_count: appointmentDestinations.length + 1,
      appointment_count: appointmentDestinations.length,
      has_active_call: false,
    };
    telehealthTracking.waitingRoomDestinationPickerOpened(pickerTelemetry);
    SentryService.addBreadcrumb({
      category: 'telehealth.waiting_room',
      message: 'Waiting room destination picker opened',
      level: 'info',
      data: pickerTelemetry,
    });
  }, [
    admitDestinationEntry,
    appointmentDestinations.length,
    enqueueSnackbar,
    isAppointmentDestinationsError,
    isLoadingAppointmentDestinations,
    isLoadingAppointmentTypes,
    isLoadingTemplates,
    launchWaitingRoomDestination,
    skipAdmitDestinationPreCallLobby,
    telehealthTracking,
  ]);

  const startWaitingRoomVideoCall = useCallback((entry: WaitingRoomEntry) => {
    openedDestinationPickerEntryRef.current = null;
    autoSelectedDestinationEntryRef.current = null;
    destinationLookupErrorEntryRef.current = null;
    setAdmitDestinationError(null);
    setAdmitDestinationEntry(entry);
  }, []);

  const joinDailyRoom = useCallback(async () => {
    if (!dailyCallServiceRef.current || !session) return;
    dispatch(exitPreCall());
    try {
      await dailyCallServiceRef.current.join(session.roomUrl, session.token);
      telehealthTracking.joinAttempted({
        appointment_id: session.appointmentId,
        encounter_id: session.encounterId,
        source: 'pre_call_lobby',
        outcome: 'connected',
      });
      telehealthTracking.callConnected({
        appointment_id: session.appointmentId,
        encounter_id: session.encounterId,
        source: 'pre_call_lobby',
      });
    } catch (error) {
      telehealthTracking.joinAttempted({
        appointment_id: session.appointmentId,
        encounter_id: session.encounterId,
        source: 'pre_call_lobby',
        outcome: 'failed',
        error_code: getTelemetryErrorCode(error),
      });
      throw error;
    }

    // Auto-start recording
    try {
      const started = await initiateRecording();
      if (!started) {
        // GDN-2784: a declined before-start gate (or a not-ready pipeline)
        // returns false rather than throwing — the call must not run
        // silently unrecorded.
        enqueueSnackbar(
          'Recording did not start automatically. You can start it manually.',
          { variant: 'warning' },
        );
      }
    } catch (recordingError) {
      SentryService.captureException(recordingError, {
        tags: { component: 'telehealth_recording_autostart' },
      });
      enqueueSnackbar('Recording failed to start automatically. You can start it manually.', {
        variant: 'warning',
      });
    }

    await applyLocalMediaPreferences(dailyCallServiceRef.current);
    await admitWaitingRoomLaunchIntent(session.encounterId);
  }, [
    applyLocalMediaPreferences,
    dispatch,
    session,
    telehealthTracking,
    initiateRecording,
    enqueueSnackbar,
    admitWaitingRoomLaunchIntent,
  ]);

  // Note: callLeft tracking is fired by CompactPipView/PipView before calling this.
  // The manager only handles cleanup — no duplicate tracking.
  const leaveCall = useCallback(() => {
    void handleLeaveInternal();
  }, [handleLeaveInternal]);

  const stopRecordingAndLeave = useCallback(async () => {
    try {
      await requestRemoteStop();
      leaveCall();
    } catch (error) {
      console.error('Failed to stop recording before leaving telehealth:', error);
      enqueueSnackbar('Failed to stop recording. Please try again.', {
        variant: 'error',
      });
    }
  }, [requestRemoteStop, leaveCall, enqueueSnackbar]);

  // --- Chat ---

  const sendChatMessage = useCallback(
    (message: ChatMessage) => {
      const co = dailyCallServiceRef.current?.getCallObject();
      if (!co) return;
      try {
        co.sendAppMessage(message, '*');
      } catch {
        // sendAppMessage may fail in local dev (postMessage origin mismatch)
      }
      dispatch(addChatMessage(message));
    },
    [dispatch],
  );

  // Listen for incoming chat messages from the call object
  useEffect(() => {
    if (!callObject) return;

    const handleAppMessage = (event: { data: unknown; fromId: string }) => {
      if (
        event.data &&
        typeof event.data === 'object' &&
        'type' in event.data &&
        (event.data as { type: string }).type === 'chat'
      ) {
        const msg = event.data as ChatMessage;
        // Use Daily's fromId as senderId fallback when the sender didn't include it
        // (e.g. Prebuilt iframe timing — session_id not yet available at send time).
        if (!msg.senderId && event.fromId) {
          msg.senderId = event.fromId;
        }
        dispatch(addChatMessage(msg));
      }
    };

    callObject.on('app-message', handleAppMessage);
    return () => {
      callObject.off('app-message', handleAppMessage);
    };
  }, [callObject, dispatch]);

  // --- Effects ---

  // Detect unexpected call drops (DailyCallService status → idle while in call)
  useEffect(() => {
    const service = dailyCallServiceRef.current;
    if (!service || phase !== 'call') return;

    const unsubscribe = service.subscribe(() => {
      const state = service.getSnapshot();
      if (state.status === 'idle') {
        if (leaveInProgressRef.current) {
          return;
        }
        // Unexpected disconnect — clean up locally only. Do NOT call
        // the end-session endpoint here: this path also catches
        // Daily reconnect-eligible drops, and we'd rather leak a
        // room until its TTL (8h) than tear down a room a provider
        // is about to rejoin. The end-session call is reserved for
        // intentful provider clicks (GDN-1538).
        void handleLeaveInternal({ explicit: false });
        enqueueSnackbar('Telehealth call ended unexpectedly.', {
          variant: 'warning',
        });
      }
    });

    return unsubscribe;
  }, [phase, handleLeaveInternal, enqueueSnackbar]);

  // Auto-close PiP when call ends (phase leaves 'call')
  useEffect(() => {
    if (phase !== 'call' && pip.isActive) {
      pip.close('call_ended');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Attach remote video track to hidden <video> for Safari classic PiP + Chrome auto-PiP
  useEffect(() => {
    if (!pip.videoRef.current) return;
    const track = remoteVideoState?.persistentTrack;
    if (track) {
      pip.videoRef.current.srcObject = new MediaStream([track]);
    } else {
      pip.videoRef.current.srcObject = null;
    }
  }, [pip.videoRef, remoteVideoState?.persistentTrack]);

  // --- Context value ---

  const contextValue: TelehealthCallManagerContextValue = {
    session,
    phase,
    activeEncounterId,
    activeAppointmentId,
    dailyCallService: dailyCallServiceRef.current,
    callObject,
    joinCall,
    joinCallDirect,
    joinWalkInCall,
    joinDailyRoom,
    leaveCall,
    stopRecordingAndLeave,
    waitingRoom,
    waitingRoomLaunchIntent,
    pip,
    sendChatMessage,
  };

  return (
    <TelehealthCallManagerContext.Provider value={contextValue}>
      {children}

      {phase === 'idle' && !pip.isActive && waitingRoom.hasAccess && (
        <WaitingRoomFloatingPanel
          waitingRoom={waitingRoom}
          onStartVideoCall={startWaitingRoomVideoCall}
        />
      )}

      {phase === 'joining' && !pip.isActive && (
        <TelehealthJoiningCallPanel patientName={waitingRoomLaunchIntent?.patientName} />
      )}

      {admitDestinationEntry && (
        <WaitingRoomAdmitDestinationModal
          open
          entry={admitDestinationEntry}
          appointments={appointmentDestinations}
          activeCall={null}
          subjectLanguage={subjectLanguage}
          skipPreCallLobby={skipAdmitDestinationPreCallLobby}
          isLoading={isLoadingAdmitDestinationData}
          isCommitting={isCommittingAdmitDestination}
          errorMessage={admitDestinationError}
          onSkipPreCallLobbyChange={handleSkipPreCallLobbyChange}
          onSubmit={handleAdmitDestinationSubmit}
          onClose={handleAdmitDestinationClose}
        />
      )}

      <DailyProvider callObject={callObject}>
        {callObject && <DailySpeakerPreferenceBridge selectedSpeakerId={selectedSpeakerId} />}

        {/* Remote audio playback — always mounted inside DailyProvider */}
        <DailyAudio />

        {/* Mini-player: visible when call active and PiP not active (all pages) */}
        {shouldShowCallPanel && session && activeEncounterId && dailyCallServiceRef.current && (
          <CallPanel
            session={session}
            activeEncounterId={activeEncounterId}
            dailyCallService={dailyCallServiceRef.current}
            isRecording={recordingStatus.isRecording}
            isRecordingStarting={recordingStatus.isStarting}
            isRecordingPaused={recordingStatus.recordingPaused}
            onLeaveCall={leaveCall}
            onPauseRecording={pauseRecording}
            onResumeRecording={resumeRecording}
          />
        )}

        {/* Document PiP portal: visible when PiP is active, regardless of page */}
        {pip.pipWindow &&
          dailyCallServiceRef.current &&
          session &&
          createPortal(
            <PipStyleProvider pipWindow={pip.pipWindow}>
              <PipErrorBoundary onClose={pip.close}>
                {pip.sizeClass === 'compact' ? (
                  <CompactPipView
                    dailyCallService={dailyCallServiceRef.current}
                    isRecording={recordingStatus.isRecording}
                    isRecordingStarting={recordingStatus.isStarting}
                    isRecordingPaused={recordingStatus.recordingPaused}
                    onLeave={() => trackCallLeftAndLeave('pip_window')}
                    onClose={handlePipClose}
                    onOpenChat={handleOpenChatFromPip}
                    onPauseRecording={pauseRecording}
                    onResumeRecording={resumeRecording}
                  />
                ) : (
                  <PipView
                    dailyCallService={dailyCallServiceRef.current}
                    audioInputDeviceService={audioInputDeviceService}
                    setSelectedMicrophone={setSelectedMicrophone}
                    pipWindow={pip.pipWindow}
                    isRecording={recordingStatus.isRecording}
                    isRecordingStarting={recordingStatus.isStarting}
                    isRecordingPaused={recordingStatus.recordingPaused}
                    session={session}
                    onLeave={() => trackCallLeftAndLeave('pip_window')}
                    onClose={handlePipClose}
                    isPipWindow
                    onPauseRecording={pauseRecording}
                    onResumeRecording={resumeRecording}
                  />
                )}
              </PipErrorBoundary>
            </PipStyleProvider>,
            pip.pipWindow.document.body,
          )}

        {/* Hidden video for Safari classic PiP + Chrome auto-PiP signal */}
        <video
          ref={pip.videoRef}
          autoPlay
          playsInline
          muted
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 1, height: 1 }}
        />
      </DailyProvider>
    </TelehealthCallManagerContext.Provider>
  );
}

function DailySpeakerPreferenceBridge({ selectedSpeakerId }: { selectedSpeakerId: string | null }) {
  const { speakers, setSpeaker } = useDevices();
  const appliedSpeakerIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedSpeakerId) return;

    const selectedSpeaker = speakers.find(s => s.device.deviceId === selectedSpeakerId);
    if (!selectedSpeaker) return;
    if (selectedSpeaker.selected && appliedSpeakerIdRef.current === selectedSpeakerId) return;

    appliedSpeakerIdRef.current = selectedSpeakerId;
    void setSpeaker(selectedSpeakerId).catch((error: unknown) => {
      appliedSpeakerIdRef.current = null;
      SentryService.captureException(error, {
        tags: { component: 'telehealth_speaker_preference' },
      });
    });
  }, [selectedSpeakerId, setSpeaker, speakers]);

  return null;
}
